import { cn } from '@/lib/utils';

import {
    LocalImage,
    type LocalImageProps,
} from '@/components/atoms/LocalImage';
import type { BusinessContactFormProps } from '@/components/organisms/BusinessContactForm';
import type { TemplateRendererProps } from '@/components/organisms/TemplateRenderer/TemplateRenderer.types';
import BasicContent, {
    type BasicContentProps,
} from '@/components/templates/BasicContent';
import BusinessContactFormTemplate from '@/components/templates/BusinessContactFormTemplate';
import CategoriesCardTemplate, {
    type CategoriesCardTemplateProps,
} from '@/components/templates/CategoriesCardTemplate';
import IconOrImageCardTemplate, {
    type IconOrImageCardTemplateProps,
} from '@/components/templates/IconOrImageCardTemplate';
import IconPointerListContent, {
    type IconPointerListContentProps,
} from '@/components/templates/IconPointerListContent';
import InfoCardTemplate, {
    type InfoCardTemplateProps,
} from '@/components/templates/InfoCardTemplate';
import IntegrationContentTemplate, {
    type IntegrationContentTemplateProps,
} from '@/components/templates/IntegrationContentTemplate';
import IntegrationSideContentTemplate, {
    type IntegrationSideContentTemplateProps,
} from '@/components/templates/IntegrationSideContentTemplate';
import OrderedCardTemplate, {
    type OrderedCardTemplateProps,
} from '@/components/templates/OrderedCardTemplate';
import ProductTileTemplate, {
    type ProductTileTemplateProps,
} from '@/components/templates/ProductTileTemplate';
import QRCodeTemplate, {
    type QRCodeTemplateProps,
} from '@/components/templates/QRCodeTemplate/QRCodeTemplate';
import RegisterFormContent, {
    type RegisterFormContentProps,
} from '@/components/templates/RegisterFormContent';
import ReviewCardTemplate, {
    type ReviewCardTemplateProps,
} from '@/components/templates/ReviewCardTemplate';
import SearchBarContent, {
    type SearchBarContentProps,
} from '@/components/templates/SearchBarContent/SearchBarContent';
import SubCategoriesCardTemplate, {
    type SubCategoriesCardTemplateProps,
} from '@/components/templates/SubCategoriesCardTemplate';
import TextContent, {
    type TextContentProps,
} from '@/components/templates/TextContent';

export default function TemplateRenderer({
    type,
    config,
}: TemplateRendererProps) {
    switch (type) {
        case 'basic-content':
            const basicContentProps = config as BasicContentProps;

            return <BasicContent {...basicContentProps} />;

        case 'business-contact-form-template':
            const businessContactFormProps = config as BusinessContactFormProps;

            return (
                <BusinessContactFormTemplate {...businessContactFormProps} />
            );

        case 'text-content':
            const textContentProps = config as TextContentProps;

            return <TextContent {...textContentProps} />;

        case 'icon-pointer-list-content':
            const listProps = config as IconPointerListContentProps;

            return <IconPointerListContent {...listProps} />;

        case 'search-bar-content':
            const searchBarProps = config as SearchBarContentProps;

            return <SearchBarContent {...searchBarProps} />;

        case 'register-form-content':
            const registerFormProps = config as RegisterFormContentProps;

            return <RegisterFormContent {...registerFormProps} />;

        case 'icon-or-image-card':
            const iconOrImageCardProps = config as IconOrImageCardTemplateProps;

            return <IconOrImageCardTemplate {...iconOrImageCardProps} />;

        case 'ordered-card':
            const orderedCardProps = config as OrderedCardTemplateProps;

            return <OrderedCardTemplate {...orderedCardProps} />;

        case 'review-card':
            const reviewCardProps = config as ReviewCardTemplateProps;

            return <ReviewCardTemplate {...reviewCardProps} />;

        case 'category-card':
            const categoryCardProps = config as CategoriesCardTemplateProps;

            return <CategoriesCardTemplate {...categoryCardProps} />;

        case 'sub-category-card':
            const subCategoryCardProps =
                config as SubCategoriesCardTemplateProps;

            return <SubCategoriesCardTemplate {...subCategoryCardProps} />;

        case 'info-card':
            const infoCardProps = config as InfoCardTemplateProps;

            return <InfoCardTemplate {...infoCardProps} />;

        case 'product-tile':
            const productTileProps = config as ProductTileTemplateProps;

            return <ProductTileTemplate {...productTileProps} />;

        case 'QR-code-content':
            const QRcodeProps = config as QRCodeTemplateProps;

            return <QRCodeTemplate {...QRcodeProps} />;

        case 'integration-side-content-template':
            const integrationSideContentProps =
                config as IntegrationSideContentTemplateProps;

            return (
                <IntegrationSideContentTemplate
                    {...integrationSideContentProps}
                />
            );

        case 'integration-content-template':
            const integrationContentProps =
                config as IntegrationContentTemplateProps;

            return <IntegrationContentTemplate {...integrationContentProps} />;

        case 'image':
            const imageProps = config as LocalImageProps;

            return (
                // eslint-disable-next-line jsx-a11y/alt-text
                <LocalImage
                    {...imageProps}
                    width={300}
                    height={300}
                    className={cn(
                        'max-h-[400px] max-w-[400px] grow object-contain lg:max-h-[556px] lg:max-w-[556px]',
                        imageProps.className,
                    )}
                    priority
                />
            );
        default:
            return null;
    }
}
